import { Option, Result } from '@sapphire/result';

import {
  changedSharedMetadata,
  mapSurveyLocalization,
  ProjectUpdatePayloadInput,
  QualificationsRecord,
  resolveTrackerChild,
  StandardSurveyPayloadInput,
  Survey,
  SurveyKind,
  SurveyLaunchForm,
  SurveyLocaleData,
  SurveyMetadataForm,
  SurveyPricingSnapshot,
  surveyToMetadataForm,
  TrackerUpdateChildInput,
  TrackerWaveFields,
} from '@ps/survey-utils';

import { SurveyAudienceForm } from '../../../utils';
import { InsightsChildState } from '../../insights/survey-creation-insights-state.model';
import { SurveyChildKey } from '../../survey-creation-shared-state.model';
import { SurveyChildEntry } from '../../survey-creation-child-registry.helper';
import { localePricing } from '../domain/child-pricing';
import { marginSnapshot } from '../domain/margin-snapshot';

export interface ProjectSaveMessages {
  readonly noChildrenSelected: string;
  readonly childNotSaveable: string;
}

export interface ProjectUpdateInput {
  /** The project row, which the root of the payload is built from. */
  readonly project: Survey;
  readonly activeMetadata: SurveyMetadataForm;
  readonly children: readonly SurveyChildEntry[];
  readonly launch: SurveyLaunchForm;
  readonly pricing: SurveyPricingSnapshot;
  readonly insightsChildren: Record<SurveyChildKey, InsightsChildState>;
  readonly qualificationsRecord: QualificationsRecord;
  readonly userId: number;
  readonly companyId: number;
  readonly enableRespondentStatusFilter: boolean;
  readonly messages: ProjectSaveMessages;
}

/**
 * The project's payload with one more child on it, cloned from the first
 * participant and re-pointed at `locale`. No `surveyId` is what asks the
 * endpoint for a new row.
 *
 * The clone is deliberate: targeting and offer_price are hoist exceptions
 * (`MC_GROUPED_UPDATE_HOIST_EXCEPTIONS`), so a child stating none would be
 * created bare rather than inheriting the group's. Anything the locales already
 * agree on hoists to the root, so the clone only decides where they differ.
 */
export function appendProjectChild(
  input: ProjectUpdateInput,
  locale: SurveyLocaleData,
): Result<ProjectUpdatePayloadInput, string> {
  return buildProjectUpdate(input).andThen((payload) =>
    Option.from(payload.children[0]).match({
      some: (source) =>
        Result.ok<ProjectUpdatePayloadInput>({
          ...payload,
          children: [
            ...payload.children,
            {
              ...source,
              surveyId: undefined,
              metadata: {
                ...source.metadata,
                selectedCountries: [locale.countryCode],
                selectedLanguages: [locale.languageShortCode],
              },
            },
          ],
        }),
      none: () => Result.err(input.messages.noChildrenSelected),
    }),
  );
}

/** The project's own body plus one entry per child, ready for the PUT. */
export function buildProjectUpdate(
  input: ProjectUpdateInput,
): Result<ProjectUpdatePayloadInput<TrackerUpdateChildInput>, string> {
  const participants = participantEntries(input);

  if (!participants.length) {
    return Result.err(input.messages.noChildrenSelected);
  }

  return Result.all(
    participants.map((entry) => projectUpdateChild(entry, input)),
  ).map((children) => ({ project: projectInput(input), children }));
}

// The registry holds the project row alongside its locales and only the
// locales are participants, so this drops the project. Registry order is kept
// as-is — it is keyed by survey id, so the first entry is the lowest-id locale,
// not the drawer's first row (`orderedChildEntries` owns that ordering).
function participantEntries({
  children,
}: ProjectUpdateInput): readonly SurveyChildEntry[] {
  return children.filter(([, , { participant }]) => !!participant);
}

// The project carries the launch settings and no targeting; its locales own the
// targeting, and will own their launch forms once that page lands.
function projectInput({
  project,
  activeMetadata,
  launch,
  pricing,
  userId,
  companyId,
  enableRespondentStatusFilter,
}: ProjectUpdateInput): StandardSurveyPayloadInput {
  const projectMetadata = surveyToMetadataForm(project);

  return {
    metadata: {
      ...projectMetadata,
      ...changedSharedMetadata(projectMetadata, activeMetadata),
    },
    pricing,
    userId,
    companyId,
    targeting: { qualifications: [], quotas: [] },
    launch,
    enableRespondentStatusFilter,
  };
}

function projectUpdateChild(
  [key, child, registry]: SurveyChildEntry,
  {
    pricing,
    insightsChildren,
    qualificationsRecord,
    userId,
    companyId,
    enableRespondentStatusFilter,
    messages,
  }: ProjectUpdateInput,
): Result<TrackerUpdateChildInput, string> {
  const form = SurveyAudienceForm.from({
    metadata: child.metadata,
    qualifications: child.qualifications,
    quotas: child.quotas,
  });

  return Option.from(registry.survey).match({
    some: (survey) =>
      Option.from(form.data).match({
        some: ({ metadata, qualifications, quotas }) =>
          Result.ok<TrackerUpdateChildInput>({
            surveyId: survey.id,
            metadata,
            pricing: localePricing(pricing, insightsChildren[key]),
            userId,
            companyId,
            targeting: {
              qualifications,
              quotas,
              catalogQualifications: [
                ...(qualificationsRecord[
                  mapSurveyLocalization(
                    metadata.selectedCountries[0] ?? '',
                    metadata.selectedLanguages[0] ?? '',
                  )
                ] ?? []),
              ],
            },
            advancedFeasibilityFilters: child.advancedFeasibilityFilters,
            enableRespondentStatusFilter,
            ...marginSnapshot(child.margin).mapOr({}, (snapshot) => ({
              margin: {
                ...snapshot,
                buyerCpi: snapshot.userAcceptedMargin
                  ? snapshot.buyerCpi
                  : null,
              },
            })),
            ...trackerWaveFields(survey),
          }),
        none: () =>
          Result.err(
            Option.from(form.error).unwrapOr(messages.childNotSaveable),
          ),
      }),
    none: () => Result.err(messages.childNotSaveable),
  });
}

function trackerWaveFields(survey: Survey): TrackerWaveFields {
  if (!SurveyKind.isTracker(survey)) {
    return {};
  }

  const trackerChild = resolveTrackerChild(survey);

  return {
    scheduleLaunch: survey.scheduleLaunch,
    billingId: survey.billingId,
    templateId: survey.templateId,
    waveId: trackerChild?.waveId,
    waveExclusions: trackerChild?.waveExclusions,
    surveyGrouping: survey.surveyGrouping,
    buyerSurveygroupRefs: survey.buyerSurveygroupRefs,
    inclExcl: survey.inclExcl,
  };
}
